import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 border-b">
        <nav className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80">
            <Sparkles className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold">AI Marketing Engine</span>
          </button>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </nav>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8">Politique de Confidentialité</h1>
        <p className="text-slate-600 mb-8">Dernière mise à jour : Janvier 2025</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              AI Marketing Engine ("nous", "notre", "nos") s'engage à protéger la vie privée de ses utilisateurs. 
              Cette politique de confidentialité explique comment nous collectons, utilisons, stockons et protégeons 
              vos informations personnelles lorsque vous utilisez notre service de marketing automatisé par intelligence artificielle.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Données collectées</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Nous collectons les types de données suivants :</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li><strong>Données d'identification :</strong> nom, adresse email, mot de passe (crypté)</li>
              <li><strong>Données de paiement :</strong> traitées de manière sécurisée par Stripe (nous ne stockons pas vos informations bancaires)</li>
              <li><strong>Données de votre site web :</strong> contenu analysé pour générer des publications pertinentes</li>
              <li><strong>Données des plateformes connectées :</strong> tokens d'accès API (cryptés) pour publier en votre nom</li>
              <li><strong>Contenus générés :</strong> textes et images créés par l'IA pour votre compte</li>
              <li><strong>Données d'utilisation :</strong> logs de connexion, préférences, statistiques d'utilisation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. Utilisation des données</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Vos données sont utilisées pour :</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li>Fournir et améliorer nos services de marketing automatisé</li>
              <li>Générer des contenus personnalisés via intelligence artificielle</li>
              <li>Publier des contenus sur vos plateformes connectées</li>
              <li>Analyser les performances de vos publications</li>
              <li>Vous contacter concernant votre compte ou nos services</li>
              <li>Traiter vos paiements et gérer votre abonnement</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. Intelligence Artificielle</h2>
            <p className="text-slate-600 leading-relaxed">
              Notre service utilise des technologies d'intelligence artificielle (OpenAI, etc.) pour analyser votre contenu 
              et générer des publications. Les données envoyées à ces services sont traitées conformément à leurs propres 
              politiques de confidentialité. Nous ne partageons que les données strictement nécessaires à la génération de contenu.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong>Important :</strong> Les contenus générés par notre plateforme sont créés par intelligence artificielle. 
              Vous êtes responsable de la validation et de la publication finale de ces contenus.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. APIs tierces</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour publier sur vos plateformes, nous utilisons leurs APIs officielles (Meta, X/Twitter, LinkedIn, Telegram, etc.). 
              Les tokens d'accès sont stockés de manière cryptée et utilisés uniquement pour les actions que vous autorisez. 
              Vous pouvez révoquer ces accès à tout moment depuis votre tableau de bord.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. Sécurité des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-4">
              <li>Chiffrement des données sensibles (mots de passe, tokens API)</li>
              <li>Connexions sécurisées (HTTPS/TLS)</li>
              <li>Accès restreint aux données personnelles</li>
              <li>Surveillance et audit des accès</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Conservation des données</h2>
            <p className="text-slate-600 leading-relaxed">
              Vos données sont conservées tant que votre compte est actif. Après suppression de votre compte, 
              vos données personnelles sont supprimées dans un délai de 30 jours, sauf obligation légale de conservation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">8. Vos droits (RGPD)</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification :</strong> corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
              <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit de limitation :</strong> limiter le traitement de vos données</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@ai-marketing-engine.com" className="text-blue-600 hover:underline">contact@ai-marketing-engine.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">9. Cookies</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous utilisons des cookies essentiels au fonctionnement du service (authentification, préférences). 
              Nous n'utilisons pas de cookies publicitaires ou de tracking tiers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">10. Modifications</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous pouvons mettre à jour cette politique de confidentialité. En cas de modification significative, 
              nous vous en informerons par email ou via une notification dans l'application.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">11. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question concernant cette politique de confidentialité ou vos données personnelles :
            </p>
            <p className="text-slate-600 mt-4">
              <strong>AI Marketing Engine</strong><br />
              Email : <a href="mailto:contact@ai-marketing-engine.com" className="text-blue-600 hover:underline">contact@ai-marketing-engine.com</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600">
          <p>© 2025 AI Marketing Engine. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
